import { CallContextPanel } from "@notifications/CallContextPanel";
import {
  CallSuggestions,
  countingDown,
  useCountdown,
} from "@notifications/CallSuggestionsPanel";
import type { IncomingCall } from "@notifications/incomingCallStore";
import {
  HeadsetButtons,
  HeadsetNotes,
  HeadsetStatus,
  HoldStrip,
  SpeakerLine,
  useHeadsetCall,
} from "@notifications/SoftphonePanel";
import { useCornerCall, useHostCallBubble } from "@notifications/useCornerCall";
import {
  type CallScheduleCell,
  type CallScheduleRead,
  type CallScheduleState,
  type CallScheduleWeek,
  useCallSchedule,
} from "@phone/callScheduleClient";
import {
  type CallSuggestionAutofile,
  type CallSuggestionsQuery,
  useCallSuggestions,
} from "@phone/callSuggestionsClient";
import {
  Button,
  Chip,
  cn,
  CrossIcon,
  formatClockTime,
  IconButton,
  SlotCell,
  SlotStrip,
} from "@scribenote/brian-design-system";
import { useState } from "react";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** A bare `YYYY-MM-DD` is a clinic-local date, not UTC midnight. */
const toDate = (value: string) =>
  new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);

const longDay = (value: string) => {
  const d = toDate(value);
  return `${WEEKDAYS[d.getDay()]} ${MONTHS[d.getMonth()]} ${d.getDate()}`;
};

const shortDay = (value: string) => {
  const d = toDate(value);
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()}`;
};

const weekOf = (value: string) => {
  const d = toDate(value);
  return `Week of ${MONTHS[d.getMonth()]} ${d.getDate()}`;
};

const clock = (value: string) => formatClockTime(toDate(value));

/** Brian's `past` label stands in for the count; a marked start replaces it. */
const cellTime = (cell: CallScheduleCell) =>
  cell.time
    ? clock(cell.time)
    : cell.label === "past"
      ? "past"
      : cell.open
        ? `${cell.open} open`
        : "full";

const cellLabel = (cell: CallScheduleCell) =>
  cell.label === "past" && !cell.time ? undefined : cell.label;

const WeekGrid = ({ week }: { week: CallScheduleWeek }) => (
  <div className="flex flex-col gap-1.5">
    <SlotStrip label={weekOf(week.start)} columns={week.days.length + 1}>
      <span aria-hidden="true" />
      {week.days.map((date) => (
        <span key={date} className="text-mono-time text-xs text-annotation">
          {shortDay(date)}
        </span>
      ))}
    </SlotStrip>
    {week.providers.map((provider) => (
      <SlotStrip
        key={provider.id}
        columns={week.days.length + 1}
        aria-label={provider.name}
      >
        <span
          className="self-center truncate text-xs font-semibold text-ink"
          title={provider.name}
        >
          {provider.shortName}
        </span>
        {provider.cells.map((cell) => {
          const label = cellLabel(cell);
          return (
            <SlotCell
              key={cell.date}
              time={cellTime(cell)}
              label={label}
              tone={cell.tone}
              title={[shortDay(cell.date), cellTime(cell), label]
                .filter(Boolean)
                .join(" · ")}
              className="h-[3.75rem] overflow-hidden"
            />
          );
        })}
      </SlotStrip>
    ))}
  </div>
);

const MovingLine = ({ state }: { state: CallScheduleState }) => {
  const existing = state.existing;
  if (!existing) return null;
  return (
    <p className="mt-1.5 truncate font-mono text-fine-print text-annotation">
      Moving from {longDay(existing.start)} · {clock(existing.start)} ·{" "}
      {existing.patient} · {existing.visitType} · {existing.provider}
    </p>
  );
};

/**
 * One week grid for the whole call: each new state retones cells and rewrites
 * the read, never the shape. A stale read keeps its picture but drops its
 * footer and confident read, since nothing on it will be written.
 */
const ScheduleSection = ({
  read: { state, stale },
}: {
  read: CallScheduleRead;
}) => {
  const heardAt = toDate(state.heard.at);
  return (
    <div>
      <p className="text-mono-caps text-xs text-annotation">
        Heard · {state.heard.speaker},{" "}
        {state.heard.side === "caller" ? "on the line" : "to the caller"} ·{" "}
        <time dateTime={state.heard.at}>{formatClockTime(heardAt)}</time>
      </p>
      <p className="mt-1.5 font-hand text-2xl leading-[1.25] text-ink-body">
        &ldquo;{state.heard.text}&rdquo;
      </p>
      <p
        aria-live="polite"
        className={`mt-3.5 border-t border-rule pt-3.5 font-display text-[1.625rem] leading-[1.2] tracking-[-0.01em] ${stale ? "text-annotation" : "text-ink"}`}
      >
        {state.read}
      </p>
      <div className="mt-3.5">
        <WeekGrid week={state.week} />
        <MovingLine state={state} />
      </div>
      <div className="mt-2.5 font-mono text-fine-print leading-normal text-annotation">
        {stale ? (
          <Chip tone="attention" size="sm">
            Brian lost the thread — say it again
          </Chip>
        ) : (
          state.footer
        )}
      </div>
    </div>
  );
};

/** Shown while proposals or the filing countdown/hold remain, including after hang-up. */
const todosOf = (query: CallSuggestionsQuery) => {
  const items = query.data?.items ?? [];
  const proposed = items.filter((item) => item.status === "proposed").length;
  const autofile = query.data?.autofile;
  const pending =
    !autofile?.filed_at &&
    (proposed > 0 || autofile?.deadline != null || autofile?.held === true);
  // A countdown starting counts as a change, so a dismissed bubble returns before Brian files.
  const version = [
    ...items
      .filter(
        (item) => item.status === "proposed" || item.status === "approved",
      )
      .map((item) => `${item.uuid}:${item.status}:${item.revision}`)
      .sort(),
    `deadline:${autofile?.deadline ?? ""}`,
  ].join("|");
  return { proposed, pending, version };
};

/**
 * The filing countdown as an accent stroke around the whole bubble, shrinking
 * from the top-left corner as the to-dos and any agreed appointment lock in.
 * The stroke sits on the border's centre line, so it covers the 1.5px border.
 */
const CountdownFrame = ({
  autofile,
  deadline,
  receivedAt,
}: {
  autofile: CallSuggestionAutofile;
  deadline: string;
  receivedAt: number;
}) => {
  const { fraction } = useCountdown(autofile, deadline, receivedAt);
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute -inset-[0.75px] h-[calc(100%+1.5px)] w-[calc(100%+1.5px)] overflow-visible"
    >
      <rect
        width="100%"
        height="100%"
        rx="17.25"
        pathLength={100}
        fill="none"
        strokeWidth={1.5}
        strokeDasharray={`${fraction * 100} 100`}
        className="stroke-accent transition-[stroke-dasharray] duration-300 ease-linear motion-reduce:transition-none"
      />
    </svg>
  );
};

/** The answered call's controls, pinned above Brian's sections so Hang up and Mute stay reachable. */
const CallSection = ({
  call,
  brianShown,
  onHideBrian,
}: {
  call: IncomingCall;
  brianShown: boolean;
  onHideBrian: () => void;
}) => {
  const headset = useHeadsetCall(call);
  const { name, number, talkTime } = headset;
  return (
    <div
      className={cn(
        "shrink-0 px-[26px] pt-4 pb-3",
        brianShown && "border-b border-rule",
      )}
      data-testid="call-bubble-call"
    >
      <div className="flex items-center gap-2">
        <HeadsetStatus headset={headset} />
        {brianShown && (
          <>
            <Chip tone="brian" size="sm">
              Brian
            </Chip>
            <IconButton
              size="sm"
              aria-label="Hide Brian's notes"
              title="Hide Brian's notes"
              onClick={onHideBrian}
              className="-mr-2"
            >
              <CrossIcon />
            </IconButton>
          </>
        )}
      </div>
      <HeadsetNotes headset={headset} />
      <div
        className="mt-2 flex items-baseline justify-between gap-3"
        data-dd-privacy="hidden"
      >
        <p className="min-w-0 truncate">
          <span className="font-display text-xl font-semibold text-ink">
            {name}
          </span>
          {number && name !== number && (
            <span className="ml-2 font-mono text-fine-print text-annotation">
              {number}
            </span>
          )}
        </p>
        {talkTime && (
          <p
            className="shrink-0 font-mono text-xl font-medium tabular-nums text-ink"
            data-testid="softphone-talk-time"
          >
            {talkTime}
            <span className="ml-1.5 align-top text-annotation-caps text-annotation">
              talk time
            </span>
          </p>
        )}
      </div>
      <div
        data-call-context-scroll
        className="max-h-[30dvh] overflow-y-auto overscroll-contain"
      >
        <CallContextPanel context={headset.context} />
      </div>
      {headset.live ? (
        <div className="mt-2.5 flex items-center gap-3">
          <SpeakerLine
            headset={headset}
            className="min-w-0 flex-1 justify-start"
          />
          <div className="flex shrink-0 gap-2">
            <HeadsetButtons headset={headset} size="sm" />
          </div>
        </div>
      ) : (
        <div className="mt-2.5 flex justify-end">
          <Button variant="ghost" size="sm" onClick={headset.dismiss}>
            Dismiss
          </Button>
        </div>
      )}
      <HoldStrip
        className={cn(
          "-mx-[26px]",
          brianShown ? "rounded-none" : "rounded-b-bubble",
        )}
      />
    </div>
  );
};

const CallBubbleFor = ({
  callUuid,
  call,
  clientName,
  ended,
}: {
  callUuid: string;
  call: IncomingCall | null;
  clientName: string | null;
  ended: boolean;
}) => {
  const schedule = useCallSchedule(callUuid);
  const query = useCallSuggestions(callUuid);
  const [dismissed, setDismissed] = useState<string | null>(null);

  const todos = todosOf(query);
  const autofile = query.data?.autofile;
  const version = `${schedule?.state.asOf ?? ""}#${todos.pending ? todos.version : ""}`;
  const brianShown =
    (schedule !== null || todos.pending) && dismissed !== version;
  if (call === null && !brianShown) return null;
  const hideBrian = () => setDismissed(version);
  const framing =
    brianShown && todos.pending && countingDown(autofile, todos.proposed)
      ? autofile
      : null;

  return (
    <section
      aria-label="On this call"
      data-testid="call-bubble"
      data-dd-privacy="hidden"
      onKeyDown={(event) => {
        if (event.key === "Escape" && brianShown) {
          event.stopPropagation();
          hideBrian();
        }
      }}
      className={cn(
        "pointer-events-auto relative flex max-h-[min(44rem,60dvh)] w-full flex-col rounded-bubble border-[1.5px] bg-card text-left shadow-bubble",
        framing ? "border-rule" : "border-accent",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "absolute right-[30px] -bottom-2 size-3.5 rotate-45 border-r-[1.5px] border-b-[1.5px] bg-card",
          framing ? "border-rule" : "border-accent",
        )}
      />
      {call ? (
        <CallSection
          call={call}
          brianShown={brianShown}
          onHideBrian={hideBrian}
        />
      ) : (
        <div className="flex items-center gap-2.5 px-[26px] pt-4 pb-2.5">
          <Chip tone="brian" size="sm">
            Brian
          </Chip>
          <p className="min-w-0 flex-1 truncate text-annotation-caps text-annotation">
            {ended ? "Call ended" : "On this call"}
            {clientName ? ` · ${clientName}` : ""}
          </p>
          <IconButton
            size="sm"
            aria-label="Close"
            title="Close"
            onClick={hideBrian}
            className="-mr-2"
          >
            <CrossIcon />
          </IconButton>
        </div>
      )}
      {brianShown && (
        <div className="min-h-0 overflow-y-auto overscroll-contain px-[26px] pb-[18px]">
          {schedule && (
            <div className={call ? "pt-3.5" : undefined}>
              <ScheduleSection read={schedule} />
            </div>
          )}
          {todos.pending && (
            <CallSuggestions
              callUuid={callUuid}
              query={query}
              title={`To-dos from this call · ${todos.proposed}`}
              framed
            />
          )}
        </div>
      )}
      {framing && (
        <CountdownFrame
          key={framing.deadline}
          autofile={framing}
          deadline={framing.deadline}
          receivedAt={query.dataUpdatedAt}
        />
      )}
    </section>
  );
};

/**
 * Brian's one bubble per inbound call. While the call is answered here its
 * controls sit pinned on top; Brian's scheduling read and the call's to-dos
 * scroll beneath as they arrive. After hang-up it stays while either still
 * needs the desk. Close/Escape hides only Brian's sections, never the call
 * controls, until either section changes.
 */
export const CallBubble = () => {
  useHostCallBubble();
  const { callUuid, call, clientName, ended } = useCornerCall();
  if (callUuid === null) return null;
  return (
    <CallBubbleFor
      key={callUuid}
      callUuid={callUuid}
      call={call}
      clientName={clientName}
      ended={ended}
    />
  );
};
